import React, { useEffect, useState, forwardRef } from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { makeStyles, withStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Tooltip from '@mui/material/Tooltip'

const useStyles = makeStyles(theme => ({
  tab: ({ orientation }) => ({
    ...(orientation === 'vertical'
      ? {
        '&:hover': {
          background: theme.backgroundLight,
          borderColor: theme.borderOutlineColor,
        },
      }
      : {
        background: theme.backgroundColor,
        border: theme.border,
        color: '#19233c',
        fontFamily: 'inherit',
        fontWeight: 'normal',
        textTransform: 'none',
        fontSize: '13px',
        opacity: 1,
        maxWidth: 'none',
        minWidth: 'auto',
        '&:hover': {
          color: theme.primaryLight,
        },
        '&:first-child': {
          borderTopLeftRadius: '4px',
        },
        '&:last-child': {
          borderTopRightRadius: '4px',
        },
        '&:not(:first-of-type)': {
          borderLeftWidth: 0,
        },
      }),
  }),
  selected: {
    background: theme.white,
    color: theme.primaryLight,
  },
  disabled: {
    background: '#eeeeee',
    color: '#bbbbbb',
  },
  labelIcon: {
    fontSize: '13px',
  },
  indicator: {
    backgroundColor: theme.primaryLight,
    height: '3px',
  },
}))

const TabInput = forwardRef((props, ref) => {
  const {
    value,
    onChange,
    muiClasses: extClasses = {},
    tabs = [],
    customClass,
    variant = 'fullWidth',
    orientation = 'horizontal',
    dataQsId,
  } = props
  const classes = useStyles(props)
  const [activeTab, setActiveTab] = useState(value || 0)

  useEffect(() => {
    if (value !== activeTab) {
      setActiveTab(value)
    }
  }, [value])

  const onHandleTabChange = (event, newValue) => {
    if (newValue === activeTab) {
      return
    }

    setActiveTab(newValue)
    onChange && onChange(newValue)
  }

  const tabElements = tabs.map((tab, index) => {
    const tabItem = (
      <Tab
        key={index}
        disableRipple={true}
        label={tab.label}
        id={index}
        disabled={tab.disabled}
        classes={{
          root: clsx(classes.tab, extClasses.tab || ''),
          labelIcon: clsx(classes.labelIcon, extClasses.labelIcon),
          selected: clsx(classes.selected, extClasses.selected),
          disabled: clsx(classes.disabled, extClasses.disabled),
        }}>
      </Tab>
    )
    return (tab.tooltipText
      ? (<LightTooltip title={tab.tooltipText} aria-label={tab.tooltipText} placement='bottom-end' className={extClasses.tooltip}>
        {tabItem}
      </LightTooltip>)
      : (tabItem))
  })
  const hasTabs = tabs.length > 0

  return (
    <div ref={ref} className={`${customClass || ''}`} data-qs-id={dataQsId}>
      <Tabs
        variant={variant}
        orientation={orientation}
        indicatorColor="secondary"
        textColor="inherit"
        classes={{
          indicator: clsx(classes.indicator, extClasses.indicator),
          root: clsx(classes.root, extClasses.root || ''),
          flexContainer: extClasses.flexContainer,
        }}
        onChange={onHandleTabChange}
        value={activeTab}>
        {tabElements}
      </Tabs>
      {hasTabs && <div className={tabs[activeTab].contentClass || ''}>
        {tabs[activeTab].content}
      </div>}
    </div>
  )
})

TabInput.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string || PropTypes.func,
    content: PropTypes.node,
  })),
  variant: PropTypes.oneOf(['fullWidth', 'scrollable', 'standard']),
  customClass: PropTypes.string,
  muiClasses: PropTypes.shape({
    root: PropTypes.string,
    tab: PropTypes.string,
    labelIcon: PropTypes.string,
    selected: PropTypes.string,
    disabled: PropTypes.string,
    tooltip: PropTypes.string,
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  dataQsId: PropTypes.string.isRequired,
}

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.grey,
    fontSize: theme.fontSizes.small,
    marginTop: 2,
    height: 26,
  },
}))(Tooltip)

export default TabInput
